'use client';

// Can probably be a server component. Must test with secondary navigation.
import { type FlexProps } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { type LinkProps } from '@ironhack/design-system2/components/link';
import { type TextProps } from '@ironhack/design-system2/components/text';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StorytellingBase } from '@/app/components/client/storytelling-base';
import { CmsImage } from '@/app/components/server/cms-image';
import {
  CmsVideo,
  type VideoPlayerData,
} from '@/app/components/server/cms-video';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms/types';

export type StorytellingCard = {
  title?: string;
  content?: StructuredTextGraphQlResponse;
};
export type Storytelling = DatoBlock<{
  columns: Array<
    Partial<{
      button: [DatoButtonBlock & Pick<LinkProps, 'rel'>];
      card: [StorytellingCard];
      flexProps: FlexProps;
      image: DatoImage | null;
      subtitle: string;
      title: string;
      video: VideoPlayerData | null;
    }>
  >;
  headingProps?: TextProps | null;
  moduleFormat: [DatoModuleFormat];
  subHeadingProps?: TextProps | null;
  subtitle?: string;
  tag?: string;
  title?: string;
}>;

type StorytellingProps = { data: Storytelling } & FlexProps;

export const Storytelling = forwardRef(
  (props: StorytellingProps, ref): ReactElement => {
    const { data, as = 'section', title: navTitle, ...flexProps } = props;
    const {
      columns,
      moduleFormat: [moduleFormat],
      headingProps,
      subHeadingProps,
      subtitle,
      tag,
      title,
    } = data;

    return (
      <ModuleWrapper
        innerProps={flexProps}
        moduleFormat={moduleFormat}
        outerProps={{
          as,
          ...flexProps,
        }}
        ref={ref}
        title={navTitle}
      >
        <StorytellingBase>
          {tag && <StorytellingBase.Tag text={tag} />}
          {title && (
            <StorytellingBase.Title as="h2" {...headingProps}>
              {title}
            </StorytellingBase.Title>
          )}
          {subtitle && (
            <StorytellingBase.Subtitle
              as="h3"
              textStyle="lBold"
              {...subHeadingProps}
            >
              {subtitle}
            </StorytellingBase.Subtitle>
          )}
          {columns.map((column, index) => (
            <StorytellingBase.Column
              key={column.title ?? index}
              {...column.flexProps}
            >
              {column.title && (
                <StorytellingBase.Title as="h2" {...headingProps}>
                  {column.title}
                </StorytellingBase.Title>
              )}
              {column.subtitle && (
                <StorytellingBase.Subtitle as="h3" {...subHeadingProps}>
                  {column.subtitle}
                </StorytellingBase.Subtitle>
              )}
              {column.video && (
                <StorytellingBase.Media>
                  <CmsVideo data={column.video} />
                </StorytellingBase.Media>
              )}
              {column.image && (
                <StorytellingBase.Media>
                  <CmsImage
                    data={column.image.responsiveImage}
                    {...(as === 'header' && { priority: true })}
                    sizes={`(min-width: 768px) ${
                      columns.length > 1 ? '548px' : '1120px'
                    }, 100vw`}
                  />
                </StorytellingBase.Media>
              )}
              {column.card?.[0] && (
                <StorytellingBase.Card
                  content={column.card[0].content}
                  title={column.card[0].title}
                />
              )}
              {column.button?.[0] && (
                <StorytellingBase.Action button={column.button[0]} />
              )}
            </StorytellingBase.Column>
          ))}
        </StorytellingBase>
      </ModuleWrapper>
    );
  }
);
